<script>
  import { matchSetup, currentScreen, activeMatch, peerRole } from '../store.js';
  import { createMatch, setOpeners } from '../../scoring-logic.js';
  import { persist } from '../../store.js';
  import { toast } from '../../ui/dialogs.js';
  import { PeerSync } from '../../peer-sync.js';
  import { applyDeviceSession } from '../../device-session.js';
  import { saveDeviceSession } from '../../session-storage.js';
  
  let formatSize = 1; // 1, 2, 3, 5
  let playerNamesA = ['Player A1'];
  let playerNamesB = ['Player B1'];
  
  $: {
    // Sync array sizes
    while (playerNamesA.length < formatSize) {
      playerNamesA.push(`Player A${playerNamesA.length + 1}`);
    }
    if (playerNamesA.length > formatSize) {
      playerNamesA = playerNamesA.slice(0, formatSize);
    }
    
    while (playerNamesB.length < formatSize) {
      playerNamesB.push(`Player B${playerNamesB.length + 1}`);
    }
    if (playerNamesB.length > formatSize) {
      playerNamesB = playerNamesB.slice(0, formatSize);
    }
  }

  const lengthOptions = [
    { label: '1 Ball', val: 1/6 },
    { label: '2 Balls', val: 2/6 },
    { label: '3 Balls', val: 3/6 },
    { label: '4 Balls', val: 4/6 },
    { label: '5 Balls', val: 5/6 },
    { label: '1 Over', val: 1 },
    { label: '2 Overs', val: 2 },
    { label: '3 Overs', val: 3 }
  ];
  let selectedLength = 1; // Default to 1 Over

  let challengeType = 'super_over'; // 'super_over', 'target_runs', 'target_sixes', 'target_wickets'
  let targetValue = 15;
  let batFirstSide = 'A'; // 'A' or 'B'
  let phoneMode = 'single_score'; // 'single_record', 'single_score', 'scorer'
  
  let isStarting = false;
  let startError = "";

  function goBack() {
    currentScreen.set('home');
  }

  async function startChallenge() {
    const A = playerNamesA.map(x => x.trim()).filter(Boolean);
    const B = playerNamesB.map(x => x.trim()).filter(Boolean);

    if (A.length < formatSize || B.length < formatSize) {
      toast("Please fill all player names.", "error");
      return;
    }

    const teamAName = formatSize === 1 ? A[0] : "Team A";
    const teamBName = formatSize === 1 ? B[0] : "Team B";

    const tossWinner = batFirstSide; // The side batting first wins the toss
    const tossDecision = "bat";

    startError = "";
    
    const newMatch = createMatch({
      teamA: { name: teamAName, players: A },
      teamB: { name: teamBName, players: B },
      overs: selectedLength,
      mode: "challenge",
      lms: true,
      toss: { winner: tossWinner, choice: tossDecision },
      challenge: {
        type: challengeType,
        targetValue: challengeType === 'super_over' ? 0 : targetValue
      }
    });

    // Set openers automatically
    const battingLetter = tossWinner;
    const bowlingLetter = tossWinner === 'A' ? 'B' : 'A';

    const striker = battingLetter === 'A' ? A[0] : B[0];
    const nonStriker = formatSize > 1 ? (battingLetter === 'A' ? A[1] : B[1]) : null;
    const bowler = bowlingLetter === 'A' ? A[0] : B[0];

    setOpeners(newMatch, striker, nonStriker, bowler);
    activeMatch.set(newMatch);

    if (phoneMode === 'single_record') {
      applyDeviceSession(newMatch, phoneMode);
      saveDeviceSession({ role: phoneMode, screen: 'recording' });
      peerRole.set(null);
      currentScreen.set('recording');
    } else if (phoneMode === 'single_score') {
      applyDeviceSession(newMatch, phoneMode);
      saveDeviceSession({ role: phoneMode, screen: 'scoring' });
      peerRole.set(null);
      currentScreen.set('scoring');
    } else if (phoneMode === 'scorer') {
      isStarting = true;
      try {
        const roomCode = await PeerSync.createRoom();
        applyDeviceSession(newMatch, phoneMode, roomCode);
        saveDeviceSession({ role: phoneMode, roomCode, screen: 'scoring' });
        currentScreen.set('scoring');
      } catch (err) {
        PeerSync.disconnect();
        activeMatch.set(null);
        startError = `Could not create recorder code: ${err?.message || 'connection server unavailable'}`;
      } finally {
        isStarting = false;
      }
    }
    persist();
  }
</script>

<div class="screen" id="setup-challenge-screen">
  <button class="btn btn-ghost" on:click={goBack}>← Back</button>
  <h1 style="margin-top:10px; color:#fbbf24;">⚡ Challenge Setup</h1>
  <p class="muted" style="margin-top:-6px; font-size:12px; margin-bottom:15px;">Setup a quick 1v1 or short format face-off match.</p>

  {#if startError}
    <div class="card" style="background:#451a03; border-color:#92400e; color:#fef3c7; font-size:12px; font-weight:700; margin-bottom:12px;">
      {startError}
    </div>
  {/if}

  <!-- FORMAT SELECTOR -->
  <div class="card">
    <div style="font-weight:800; margin-bottom:8px; letter-spacing:1px; font-size:12px; color:#94a3b8; text-transform:uppercase;">Challenge Format</div>
    <div style="display:flex; gap:8px;">
      {#each [1, 2, 3, 5] as size, i_each (size.id || size.name || i_each)}
        <button 
          class="btn {formatSize === size ? 'active' : ''}" 
          style="flex:1; padding: 10px; font-weight:800; border: 1px solid {formatSize === size ? '#fbbf24' : '#334155'}; background: {formatSize === size ? '#fbbf24' : '#1e293b'}; color: {formatSize === size ? '#1e293b' : '#fff'};"
          on:click={() => formatSize = size}>
          {size} vs {size}
        </button>
      {/each}
    </div>
  </div>

  <!-- PLAYER NAMES INPUTS -->
  <div class="card">
    <div style="font-weight:800; margin-bottom:12px; letter-spacing:1px; font-size:12px; color:#fbbf24; text-transform:uppercase;">Player Names Setup</div>
    
    <div style="display:grid; grid-template-columns: 1fr 1fr; gap:16px;">
      <!-- TEAM A -->
      <div>
        <div style="font-size:11px; font-weight:800; color:#cbd5e1; margin-bottom:8px; text-transform:uppercase;">Side A</div>
        {#each Array(formatSize) as _, i (i)}
          <div style="margin-bottom:8px;">
            <input 
              type="text" 
              placeholder="Player A{i+1} Name" 
              bind:value={playerNamesA[i]} 
              style="width: 100%; padding: 10px; background: #0f172a; border: 1px solid #334155; color: white; border-radius: 6px; font-size: 14px;" />
          </div>
        {/each}
      </div>

      <!-- TEAM B -->
      <div>
        <div style="font-size:11px; font-weight:800; color:#cbd5e1; margin-bottom:8px; text-transform:uppercase;">Side B</div>
        {#each Array(formatSize) as _, i (i)}
          <div style="margin-bottom:8px;">
            <input 
              type="text" 
              placeholder="Player B{i+1} Name" 
              bind:value={playerNamesB[i]} 
              style="width: 100%; padding: 10px; background: #0f172a; border: 1px solid #334155; color: white; border-radius: 6px; font-size: 14px;" />
          </div>
        {/each}
      </div>
    </div>
  </div>

  <!-- MATCH LENGTH OPTIONS -->
  <div class="card">
    <div style="font-weight:800; margin-bottom:10px; letter-spacing:1px; font-size:12px; color:#94a3b8; text-transform:uppercase;">Match Length</div>
    <div style="display:grid; grid-template-columns: repeat(4, 1fr); gap:6px;">
      {#each lengthOptions as opt, i_each (opt.id || opt.name || i_each)}
        <button 
          class="btn {selectedLength === opt.val ? 'active' : ''}" 
          style="padding: 8px 4px; font-size:11px; font-weight:800; border: 1px solid {selectedLength === opt.val ? '#fbbf24' : '#334155'}; background: {selectedLength === opt.val ? '#fbbf24' : '#1e293b'}; color: {selectedLength === opt.val ? '#1e293b' : '#fff'};"
          on:click={() => selectedLength = opt.val}>
          {opt.label}
        </button>
      {/each}
    </div>
  </div>

  <!-- CHALLENGE SETTINGS -->
  <div class="card">
    <div class="field">
      <label for="challenge-select" style="font-size:11px; text-transform:uppercase; color:#9ca3af; font-weight:800; margin-bottom:4px; display:block;">Challenge Type</label>
      <select id="challenge-select" bind:value={challengeType} style="width:100%; padding:10px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px; font-size:14px; font-weight:800;">
        <option value="super_over">Super Over (Play & Chase)</option>
        <option value="target_runs">Target: Score Runs</option>
        <option value="target_sixes">Target: Hit Sixes</option>
        <option value="target_wickets">Target: Take Wickets</option>
      </select>
    </div>

    {#if challengeType !== 'super_over'}
      <div class="field" style="margin-top:12px;">
        <label for="challenge-target" style="font-size:11px; text-transform:uppercase; color:#9ca3af; font-weight:800; margin-bottom:4px; display:block;">Target Value</label>
        <input type="number" id="challenge-target" bind:value={targetValue} min="1" max="100" style="width: 100%; padding: 10px; background: #0f172a; border: 1px solid #334155; color: white; border-radius: 6px; font-size: 14px;" />
      </div>
    {/if}
  </div>

  <!-- TOSS / BAT FIRST -->
  <div class="card">
    <div class="field">
      <div id="toss-label" style="font-size:11px; text-transform:uppercase; color:#9ca3af; font-weight:800; margin-bottom:8px; display:block;">Who will Bat First?</div>
      <div style="display:flex; gap:8px;">
        <button 
          class="btn {batFirstSide === 'A' ? 'active' : ''}" 
          style="flex:1; padding: 10px; font-weight:800; border: 1px solid {batFirstSide === 'A' ? '#fbbf24' : '#334155'}; background: {batFirstSide === 'A' ? '#fbbf24' : '#1e293b'}; color: {batFirstSide === 'A' ? '#1e293b' : '#fff'};"
          on:click={() => batFirstSide = 'A'}>
          {formatSize === 1 ? (playerNamesA[0] || 'Side A') : 'Side A'}
        </button>
        <button 
          class="btn {batFirstSide === 'B' ? 'active' : ''}" 
          style="flex:1; padding: 10px; font-weight:800; border: 1px solid {batFirstSide === 'B' ? '#fbbf24' : '#334155'}; background: {batFirstSide === 'B' ? '#fbbf24' : '#1e293b'}; color: {batFirstSide === 'B' ? '#1e293b' : '#fff'};"
          on:click={() => batFirstSide = 'B'}>
          {formatSize === 1 ? (playerNamesB[0] || 'Side B') : 'Side B'}
        </button>
      </div>
    </div>
  </div>

  <!-- PHONE RECORDING MODE -->
  <div class="card">
    <div class="field">
      <div id="phone-mode-label" style="font-size:11px; text-transform:uppercase; color:#9ca3af; font-weight:800; margin-bottom:8px; display:block;">Recording Mode</div>
      <select bind:value={phoneMode} style="width:100%; padding:10px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px; font-size:14px; font-weight:800;">
        <option value="single_score">Single Phone: Scorecard Only</option>
        <option value="single_record">Single Phone: Score + Camera Record</option>
        <option value="scorer">Two-Phone Mode: Scorer Phone</option>
      </select>
    </div>
  </div>

  <!-- LAUNCH -->
  <button class="btn btn-primary btn-block" style="padding: 16px; font-size: 16px; font-weight: 800; background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); border: none;" on:click={startChallenge} disabled={isStarting}>
    {isStarting ? 'Setting up code...' : '▶  START CHALLENGE'}
  </button>
</div>
